import { useMemo } from 'react'
import { BasicInformatioTableDef } from '../../../../../containers/SingleSheetV2/table/model'
import {
  useCode,
  useDisplayName,
  useStatus,
  useSubStatus,
  useTags,
  useTeam,
  useAccountable,
  useResponsible,
  useAssignee,
  useWatchers,
  useScheduledDate,
  useActualDate,
  useCreatedBy,
  useCreatedAt,
  useUpdatedBy,
  useUpdatedAt,
  useRevision,
  useType,
} from './cell-def/wbsItem'
import {
  useAssignment,
  useBasicInformation,
  usePlanning,
  useUpdatedInfo,
} from './table-header-def/wbsItem'
import { useWbsItemTagSearch } from '../../../hooks/wbsItemTagSearch'
import { WbsItemPropertySearch } from '../../../hooks/wbsItemPropertySearch'
import { WbsItemSelectOptions } from '../../../hooks/wbsItemSelectOptions'
import { useBasicInformationTableComposer } from './table-composer'
import { WbsItemAdditionalPropertyLayoutEntity } from '../../../../../../domain/entity/WbsItemAdditionalPropertyLayoutEntity'
import { FunctionPropertyConfigurationState } from '../../../hooks/functionPropertyConfiguration'

export const useProcessBasicInformationTableDef = (
  projectUuid: string,
  { subStatuses }: Pick<WbsItemSelectOptions, 'subStatuses'>,
  {
    searchTeam,
    searchMember,
  }: Pick<WbsItemPropertySearch, 'searchTeam' | 'searchMember'>,
  wbsItemAdditionalProperties:
    | WbsItemAdditionalPropertyLayoutEntity
    | undefined,
  functionPropertyConfiguration: FunctionPropertyConfigurationState
): BasicInformatioTableDef => {
  // Table groups
  const basicInformationGroup = useBasicInformation()
  const assignmentGroup = useAssignment()
  const planningGroup = usePlanning()
  const updatedInfoGroup = useUpdatedInfo()
  // Hooks for table cells
  const searchTag = useWbsItemTagSearch(projectUuid)
  // Table cells
  const code = useCode()
  const displayName = useDisplayName()
  const status = useStatus()
  const substatus = useSubStatus(subStatuses)
  const type = useType() // TODO Remove. please refer comments inside code.
  const tags = useTags(searchTag)
  const team = useTeam(searchTeam)
  const accountable = useAccountable()
  const responsible = useResponsible()
  const assignee = useAssignee()
  const watchers = useWatchers(searchMember)
  const scheduledDate = useScheduledDate()
  const actualDate = useActualDate()
  const createdBy = useCreatedBy()
  const createdAt = useCreatedAt()
  const updatedBy = useUpdatedBy()
  const updatedAt = useUpdatedAt()
  const revision = useRevision()

  const defaultTableDef = useMemo<BasicInformatioTableDef>(() => {
    return {
      tables: [
        {
          header: basicInformationGroup,
          rows: [[code], [displayName], [status], [substatus], [type], [tags]],
        },
        {
          header: assignmentGroup,
          rows: [[team], [accountable], [responsible], [assignee], [watchers]],
        },
        {
          header: planningGroup,
          rows: [[scheduledDate], [actualDate]],
        },
        {
          header: updatedInfoGroup,
          rows: [[createdBy, createdAt], [updatedBy, updatedAt], [revision]],
        },
      ],
    }
  }, [
    basicInformationGroup,
    assignmentGroup,
    planningGroup,
    updatedInfoGroup,
    code,
    displayName,
    status,
    substatus,
    type,
    tags,
    team,
    accountable,
    responsible,
    assignee,
    watchers,
    scheduledDate,
    actualDate,
    createdBy,
    createdAt,
    updatedBy,
    updatedAt,
    revision,
  ])

  const basicInformationTableDef = useBasicInformationTableComposer(
    projectUuid,
    defaultTableDef,
    wbsItemAdditionalProperties,
    functionPropertyConfiguration
  )

  return basicInformationTableDef
}
